import { CurrencyPipe, DecimalPipe } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import {
	ChangeDetectionStrategy,
	Component,
	ErrorHandler,
	OnDestroy,
	booleanAttribute,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core'
import { BankOfferExtraCurrencyTypes, BankOfferTypes, ShopBankOfferDto, isEventType } from '@mos/api/bank/dto-client'
import { NotificationType } from '@mos/api/notification/dto-client'
import { IconComponent } from '@mos/client/shared/icons'
import { IconShopVideo } from '@mos/client/shared/icons-collection'
import { LEAVE_SYNC_CLASS, LeaveSyncDirective } from '@mos/client/shared/utils'
import { CurrencyIconComponent } from '@mos/client/web/core/components/currency-icon/currency-icon.component'
import { AdRewardService } from '@mos/client/web/shop/data-access/services/ads/ad-reward.service'
import { ShopService } from '@mos/client/web/shop/data-access/services/shop.service'
import { AuthRepository } from '@mos/shared/auth'
import { isCurrency } from '@mos/shared/types'
import { take } from 'rxjs'
import { OfferProductComponent } from '../offer-product/offer-product.component'

@Component({
	selector: 'mos-offer',
	imports: [
		IconComponent,
		CurrencyIconComponent,
		OfferProductComponent,
		DecimalPipe,
		CurrencyPipe,
		LeaveSyncDirective,
	],
	templateUrl: './offer.component.html',
	styleUrl: './offer.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	// Widely-reused leaf atom (every shop modal/page renders one or more) - hostDirectives applied
	// proactively per docs/animations-migration.md's policy note.
	hostDirectives: [LeaveSyncDirective],
	providers: [{ provide: LEAVE_SYNC_CLASS, useValue: 'offer-out' }],
	host: {
		'[class]': 'classes',
		'(click)': 'onClick()',
		'[attr.aria-busy]': 'pending()',
		'animate.enter': 'offer-in',
	},
})
export class OfferComponent implements OnDestroy {
	get classes(): string {
		return [
			this.offer().type,
			this.disabled() || this.remainingTime() === 0 ? 'disabled' : '',
			this.pending() ? 'pending' : '',
			this.otherOfferPending() ? 'other-pending' : '',
		].join(' ')
	}

	icons = {
		video: IconShopVideo,
	}

	consumed = output()

	disabled = input(false, { transform: booleanAttribute })
	limitedOffer = input<boolean>(false)
	offer = input.required<ShopBankOfferDto>()
	maxVideoRewardedReached = input<boolean>(false)
	private readonly shopService = inject(ShopService)
	protected readonly BankOfferTypes = BankOfferTypes
	protected readonly BankOfferExtraCurrencyTypes = BankOfferExtraCurrencyTypes
	private readonly adRewardService = inject(AdRewardService)
	private authRepository = inject(AuthRepository)
	private readonly errorHandler = inject(ErrorHandler)

	// On native builds the gap between the tap and the OS purchase sheet showing up is seconds long, with
	// nothing happening on screen otherwise - players read that as a dead button and tap again.
	protected readonly pending = computed(() => this.shopService.pendingOfferId() === this.offer().id)
	protected readonly otherOfferPending = computed(() => this.shopService.paymentInitPending() && !this.pending())

	private animationFrameId?: number

	// The label only has second-level granularity, so there's no point re-computing/re-rendering on every
	// single animation frame (60x/s) — the rAF loop keeps running (so it still pauses on background tabs),
	// but the signal is only written once a second has actually elapsed.
	private static readonly TICK_INTERVAL_MS = 1000

	decrementRemainingTime = effect(() => {
		const offer = this.offer()
		this.remainingTime.set(offer.remainingTime)

		this.clearAnimation()
		if (!offer.remainingTime) {
			return
		}

		const startedAt = Date.now()
		const initialRemainingTime = offer.remainingTime
		let lastTickElapsed = 0

		const tick = () => {
			const elapsed = Date.now() - startedAt
			if (elapsed - lastTickElapsed < OfferComponent.TICK_INTERVAL_MS) {
				this.animationFrameId = requestAnimationFrame(tick)
				return
			}
			lastTickElapsed = elapsed

			const newTime = initialRemainingTime - elapsed
			if (newTime <= 0) {
				this.remainingTime.set(0)
				return
			}
			this.remainingTime.set(newTime)
			this.animationFrameId = requestAnimationFrame(tick)
		}
		this.animationFrameId = requestAnimationFrame(tick)
	})

	remainingTime = signal<number | undefined>(undefined)
	remainingTimeLabel = computed(() => {
		const time = this.remainingTime()
		if (time === undefined) return undefined

		const timeInSeconds = time / 1000

		const day = Math.floor(timeInSeconds / (3600 * 24))
		const hours = Math.floor((timeInSeconds % (3600 * 24)) / 3600)
		const minutes = Math.floor((timeInSeconds % 3600) / 60)
		const seconds = Math.floor(timeInSeconds % 60)

		if (day > 0) {
			return (
				day.toLocaleString(undefined, { minimumIntegerDigits: 2, useGrouping: false }) +
				'j ' +
				hours.toLocaleString(undefined, { minimumIntegerDigits: 2, useGrouping: false }) +
				'h'
			)
		}

		if (hours > 0) {
			return (
				hours.toLocaleString(undefined, { minimumIntegerDigits: 2, useGrouping: false }) +
				'h ' +
				minutes.toLocaleString(undefined, { minimumIntegerDigits: 2, useGrouping: false }) +
				'm'
			)
		}

		if (minutes > 0) {
			return (
				minutes.toLocaleString(undefined, { minimumIntegerDigits: 2, useGrouping: false }) +
				'm ' +
				seconds.toLocaleString(undefined, { minimumIntegerDigits: 2, useGrouping: false }) +
				's'
			)
		}

		return seconds + 's'
	})

	onClick() {
		if (this.disabled() || this.remainingTime() === 0 || this.shopService.paymentInitPending()) return

		const offer = this.offer()
		this.shopService.pendingOfferId.set(offer.id)

		if (offer.currency === BankOfferExtraCurrencyTypes.ADVERTISEMENT) {
			this.adRewardService.playAd()
			this.shopService.pendingOfferId.set(null)
			return
		}

		if (offer.currency === BankOfferExtraCurrencyTypes.KEYS) {
			this.shopService.consumeKeyOffer(offer.id).subscribe({
				next: () => this.shopService.pendingOfferId.set(null),
				error: (error) => this.handlePurchaseError(error),
			})
			return
		}

		if (offer.currency === BankOfferExtraCurrencyTypes.FREE_ONCE) {
			this.shopService.consumeFreeOffer(offer.id).subscribe({
				next: () => {
					this.authRepository.consumePlayerFreeOffer()
					this.shopService.pendingOfferId.set(null)
				},
				error: (error) => this.handlePurchaseError(error),
			})
			return
		}

		if (isCurrency(offer.currency)) {
			this.shopService
				.consumeOffer(offer)
				.pipe(take(1))
				.subscribe({
					next: (checkout) => {
						this.consumed.emit()
						if (checkout !== undefined) {
							window.location.href = checkout.url
						}
					},
					error: (error) => this.handlePurchaseError(error),
				})
			return
		}

		this.shopService.pendingOfferId.set(null)
	}

	// ShopService already resets the pending state on error. API refusals the notification interceptor turned
	// into a modal (not enough keys/tickets) are handled from the player's point of view; anything else still
	// goes to the global ErrorHandler (and Sentry) instead of escaping as an unhandled error.
	private handlePurchaseError(error: unknown) {
		const handledCauses: unknown[] = [NotificationType.NOT_ENOUGH_MEMORY_KEY, NotificationType.NOT_ENOUGH_TICKET]
		if (error instanceof HttpErrorResponse && handledCauses.includes(error.error?.cause)) {
			return
		}
		this.errorHandler.handleError(error)
	}

	ngOnDestroy() {
		this.clearAnimation()
	}

	private clearAnimation() {
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId)
			this.animationFrameId = undefined
		}
	}

	protected readonly isEventType = isEventType
}
